import { AxiosResponse } from 'axios';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useLoginStore } from './login.store';
import { resetToken, setTokenReady } from '@/shared/utils/wait-for-token';
import { lcmsApi, vivasamApi } from '@/shared/api/common';
import { loginApi } from '@/shared/api/login';

type StorageType = 'local' | 'session';
const getStorage = (type: StorageType) => {
    return type == 'session' ? sessionStorage : localStorage;
};

const storage = (type: StorageType) => {
    const _storage = getStorage(type);
    return {
        set: (key: string, data: any) => {
            if (data != undefined) {
                _storage.setItem(key, JSON?.stringify(data));
            }
        },
        get: <T>(key: string, defaultVal: T): T => {
            const data = _storage.getItem(key);
            if (data == null) return defaultVal;
            return JSON?.parse(data) as T;
        },
    };
};
const local = storage('local');
const session = storage('session');

interface StoreState {
    sUserId: string;
    sDefaultUserId: string;
    sIsLogin: FlagYn | 'L';
    sSchCode: string | null;
    sTextbookId: string | null;
    sBrandId: number | null;
    sToken: string | null;
    sRefreshToken: string | null;
    sVivaToken: string | null;
    hmac: string | null;
    sMyClassMemberList: API.MyClassMemberList.ClassData[] | null;
    memberList: API.MyClassMemberList.MemberInfo[] | null;
    sCurrentTime: string;
    classMemberListRefreshTrigger: number;

    setSUserId: () => void;
    setSDefaultUserId: () => void;
    setSIsLogin: () => void;
    setSIsLogout: () => void;
    setSSchCode: () => void;
    setSTextbookId: (res: AxiosResponse<API.MyClassMemberList.Res, any>) => void;
    setSToken: (token: string | null) => void;
    setSRefreshToken: (rToken: string | null) => void;
    setSHmac: (rToken: string | null) => void;
    setSBrandId: () => Promise<void>;
    setSMyClasses: (params: API.MyclassClasses.Req, token: string) => Promise<void>;
    setSMyClassMemberList: (params: API.MyClassMemberList.Req, token: string) => Promise<void>;
    setSGetClassInfo: (params: API.GetClassInfo.Req, token: string) => Promise<void>;
    setSAuthLogin: (params: API.AuthLogin.Req) => Promise<void>;
    printValues: () => void;
    refreshAccessToken: () => Promise<API.Response<API.CommonJwtRefresh.Res> | undefined>;
    setPersistToken: (jwt: any) => Promise<void>;
    setSVivaToken: (token: string) => void;
    setSCurrentTime: (value: string) => void;
    setClassMemberListRefreshTrigger: (value: number) => void;
    setPersistSUserId: (value: string) => void;
    setPersistSDefaultUserId: (value: string) => void;
}

export const usePersistStore = create<StoreState>()(
    persist(
        (set, get) => ({
            sUserId: '',
            sDefaultUserId: '',
            sIsLogin: 'N',
            sSchCode: null,
            sBrandId: null,
            sTextbookId: null,
            sToken: null,
            sRefreshToken: null,
            sVivaToken: null,
            hmac: null,
            sMyClassMemberList: null,
            sCurrentTime: session.get('sCurrentTime', ''),
            memberList: null, // 유저 이름 매칭을 위해 유저만 따로 모아놓은 리스트
            classMemberListRefreshTrigger: 0,

            setSUserId: () =>
                set({ sUserId: 'viva-' + useLoginStore.getState().loginInfo.memberId + '-t' }),
            setSDefaultUserId: () =>
                set({ sDefaultUserId: useLoginStore.getState().loginInfo.memberId }),
            setSIsLogin: () => set({ sIsLogin: useLoginStore.getState().loginInfo.loginYn }),
            setSIsLogout: () => {
                sessionStorage.removeItem('persist-token');
                set({ sIsLogin: 'N' });
                resetToken();
                window.location.href = '/create';
            },
            setSSchCode: () => set({ sSchCode: useLoginStore.getState().loginInfo.schCode }),
            setSTextbookId: () => set({ sTextbookId: useLoginStore.getState().loginInfo.schCode }),
            setSToken: (token) => {
                // sessionStorage.setItem('accessToken', token!);
                set({ sToken: token });
            },
            setSRefreshToken: (rToken) => {
                // sessionStorage.setItem('refreshToken', rToken!);
                set({ sRefreshToken: rToken });
            },
            setSHmac: (token) => {
                // sessionStorage.setItem('hmac', token!);
                set({ hmac: token });
            },
            setSCurrentTime: (value) => {
                session.set('sCurrentTime', value);
                set({ sCurrentTime: value });
            },

            setSBrandId: async () => {
                const res = await lcmsApi.getBrandId();
                if (res.data) {
                    const brand = res.data?.vo.find((item) => item.code === 'VIVA01');
                    // 6:
                    if (brand?.id === 6) {
                        set({ sBrandId: brand.id });
                    } else {
                        set({ sBrandId: 6 });
                    }
                }
            },
            setSMyClasses: async (params, token) => {
                const res = await vivasamApi.getMyClasses(params, token);
                if (res) {
                }
            },
            // 클래스 및 학생
            setSMyClassMemberList: async (params, token) => {
                const res = await vivasamApi.getMyClassMemberList(params, token);
                if (res) {
                    set({ sMyClassMemberList: res.data.response });
                    set({ memberList: res.data.response.map((e) => e?.memberInfo ?? [])?.flat() });
                }
            },
            setSGetClassInfo: async (params, token) => {
                const res = await vivasamApi.getClassInfo(params, token);
                if (res) {
                }
            },
            //토큰
            setSAuthLogin: async (params) => {
                const res = await vivasamApi.getAuthLogin(params);
                if (res) {
                    get().setSVivaToken(res.response);
                }
            },

            printValues: () => {
                const currentState = get();
                Object.entries(currentState).forEach(([key, value]) => {
                    if (typeof value !== 'function') {
                        // console.log(`!!! persist: ${key}:`, value);
                    }
                });
            },

            refreshAccessToken: async () => {
                try {
                    const { sRefreshToken } = get();
                    if (!sRefreshToken) throw new Error('No refresh token available');

                    const res = await loginApi.refreshJwtToken({ refreshToken: sRefreshToken });

                    if (res) {
                        get().setPersistToken(res);
                        get().setSAuthLogin({
                            id: 'vivaclass',
                            accessKey: import.meta.env.VITE_VIVACLASS_ACCESS_KEY,
                        });
                    }
                    return res;
                } catch (error) {
                    console.error('토큰 갱신 실패:', error);
                    // 토큰 갱신 실패 시 로그아웃
                    sessionStorage.removeItem('persist-token');
                    window.location.href = '/create';
                    return;
                }
            },

            setPersistToken: async (jwt: any) => {
                get().setSToken(jwt.accessToken);
                get().setSRefreshToken(jwt.refreshToken);
                get().setSHmac(jwt.hmac);
                setTokenReady(jwt.accessToken);
            },

            setSVivaToken: async (token: string) => {
                set({ sVivaToken: token });
            },

            setClassMemberListRefreshTrigger: (value: number) => {
                set({ classMemberListRefreshTrigger: value });
            },

            setPersistSUserId: (value: string) => {
                set({ sUserId: value });
            },
            setPersistSDefaultUserId: (value: string) => {
                set({ sDefaultUserId: value });
            },
        }),
        {
            name: 'persist-token', // 세션 스토리지에 저장될 key
            storage: createJSONStorage(() => sessionStorage), // (optional)이기 때문에 해당 줄을 적지 않으면 'localStorage'가 기본 저장소로 사용된다.
            partialize: (state) => ({
                sUserId: state.sUserId,
                sToken: state.sToken,
                sRefreshToken: state.sRefreshToken,
                sHmac: state.hmac,
                sVivaToken: state.sVivaToken,
            }),
        }
    )
);
